// src/components/character/creation/steps/Skills.js
import './Skills.css';
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Plus, Minus, RefreshCw, Info, AlertTriangle,
  Sword, Scroll, Users, Compass, BookOpen, Hammer
} from 'lucide-react';
import { 
  getSkillAttribute, 
  getSkillUpgradeCost, 
  calculateSpentSkillPoints 
} from '..';

// Skill category icons
const CATEGORY_ICONS = {
  combat: Sword,
  magic: Scroll,
  social: Users,
  survival: Compass,
  knowledge: BookOpen,
  crafting: Hammer
};

// Human-readable category names
const CATEGORY_NAMES = {
  combat: "Combat",
  magic: "Magic",
  social: "Social",
  survival: "Survival",
  knowledge: "Knowledge",
  crafting: "Crafting"
};

// Skill descriptions
const SKILL_DESCRIPTIONS = {
  combat: {
    oneHandedWeapons: "Proficiency with one-handed weapons like swords, axes, and maces",
    twoHandedWeapons: "Ability to effectively wield large two-handed weapons",
    archery: "Accuracy with bows and crossbows at range",
    thrown: "Skill with thrown weapons like daggers, javelins, and axes",
    defense: "Ability to avoid damage and use armor effectively",
    unarmedCombat: "Fighting with fists, kicks, and grappling techniques"
  },
  magic: {
    elementalMagic: "Control over fire, water, earth, and air elements",
    healingMagic: "Restoration of health and curing of ailments",
    divination: "Ability to see the future and gain insights",
    enchantment: "Magic that influences minds and enhances objects",
    necromancy: "Magic involving life forces, death, and undeath"
  },
  social: {
    persuasion: "Convincing others through logical or emotional appeals",
    intimidation: "Using fear or threats to get what you want",
    deception: "Lying convincingly and misdirecting others",
    diplomacy: "Negotiating agreements and resolving conflicts",
    trading: "Buying, selling, and bartering for better prices"
  },
  survival: {
    tracking: "Following trails and finding creatures or people",
    trapping: "Setting snares and catching game or enemies",
    foraging: "Finding food, water, and useful plants in the wild",
    navigation: "Finding your way using landmarks, stars, or maps",
    stealth: "Moving quietly and remaining unseen"
  },
  knowledge: {
    history: "Understanding past events and their significance",
    arcana: "Knowledge of magic theory, artifacts, and mystical traditions",
    nature: "Understanding plants, animals, and natural phenomena",
    religion: "Knowledge of deities, rituals, and religious organizations",
    engineering: "Understanding of structures, mechanisms, and design"
  },
  crafting: {
    alchemy: "Creating potions, poisons, and chemical compounds",
    smithing: "Forging metal into weapons, armor, and tools",
    woodworking: "Crafting items from wood like bows or furniture",
    leatherworking: "Creating leather armor, bags, and other items",
    cooking: "Preparing food for sustenance and special effects"
  }
};

// Formatting helper for skill names
const formatSkillName = (name) => {
  return name
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
};

export const Skills = ({ character, onUpdate, onError, onSuccess }) => {
  const [remainingPoints, setRemainingPoints] = useState(0);
  const [skillInfo, setSkillInfo] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  
  // Calculate skill points spent and remaining
  useEffect(() => {
    const spent = calculateSpentSkillPoints(character.skills);
    setRemainingPoints(character.skillPoints - spent);
  }, [character.skills, character.skillPoints]);
  
  // Create skill info mapping with associated attribute, etc.
  useEffect(() => {
    const info = {};
    Object.entries(character.skills).forEach(([category, skills]) => {
      info[category] = {};
      Object.keys(skills).forEach(skillName => {
        const associatedAttribute = getSkillAttribute(category, skillName);
        const attributeValue = character.attributes[associatedAttribute] || 10;
        const attributeModifier = Math.floor((attributeValue - 10) / 2);
        
        info[category][skillName] = {
          rank: character.skills[category][skillName],
          attribute: associatedAttribute,
          attributeValue: attributeValue,
          attributeModifier: attributeModifier,
          upgradeCost: getSkillUpgradeCost(character.skills[category][skillName])
        };
      });
    });
    setSkillInfo(info);
  }, [character.skills, character.attributes]);
  
  // Increase skill rank
  const increaseSkill = useCallback((category, skill) => {
    const currentRank = character.skills[category][skill];
    const cost = getSkillUpgradeCost(currentRank);
    
    // Check if we have enough points and aren't at max rank
    if (cost === -1 || currentRank >= 5) {
      onError(`${formatSkillName(skill)} is already at maximum rank (5)`);
      return;
    }
    
    if (cost > remainingPoints) {
      onError(`Not enough skill points. Upgrading ${formatSkillName(skill)} requires ${cost} points, but you only have ${remainingPoints}`);
      return;
    }
    
    // Update the skill
    const updatedSkills = { ...character.skills };
    updatedSkills[category][skill] = currentRank + 1;
    
    onUpdate({ skills: updatedSkills });
    onSuccess(`Increased ${formatSkillName(skill)} to rank ${currentRank + 1}`);
  }, [character.skills, remainingPoints, onUpdate, onError, onSuccess]);
  
  // Decrease skill rank
  const decreaseSkill = useCallback((category, skill) => {
    const currentRank = character.skills[category][skill];
    
    if (currentRank <= 0) {
      onError(`${formatSkillName(skill)} is already at minimum rank (0)`);
      return;
    }
    
    // Update the skill
    const updatedSkills = { ...character.skills };
    updatedSkills[category][skill] = currentRank - 1;
    
    onUpdate({ skills: updatedSkills });
    onSuccess(`Decreased ${formatSkillName(skill)} to rank ${currentRank - 1}`);
  }, [character.skills, onUpdate, onError, onSuccess]);
  
  // Reset all skills to zero
  const resetSkills = useCallback(() => {
    const resetSkills = {};
    Object.keys(character.skills).forEach(category => {
      resetSkills[category] = {};
      Object.keys(character.skills[category]).forEach(skill => {
        resetSkills[category][skill] = 0;
      });
    });
    
    onUpdate({ skills: resetSkills });
    onSuccess('All skills have been reset');
  }, [character.skills, onUpdate, onSuccess]);
  
  // Render skill rank bar with indicators
  const SkillRankBar = ({ rank }) => {
    return (
      <div className="skill-rank-bar">
        {[...Array(5)].map((_, i) => (
          <div 
            key={i} 
            className={`skill-rank-indicator ${i < rank ? 'filled' : ''}`}
          />
        ))}
      </div>
    );
  };
  
  // Skill category component
  const SkillCategory = ({ category, skills }) => {
    const Icon = CATEGORY_ICONS[category] || BookOpen;
    
    return (
      <motion.div
        className="skills-category"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="skills-category__header">
          <Icon size={24} />
          <h3 className="skills-category__title">{CATEGORY_NAMES[category]}</h3>
        </div>
        
        <div className="skills-category__content">
          {Object.entries(skills).map(([skillName, info]) => (
            <div key={skillName} className="skill-item">
              <div className="skill-item__header">
                <div className="skill-item__name-container">
                  <span className="skill-item__name">{formatSkillName(skillName)}</span>
                  <span className="skill-item__attribute">
                    ({info.attribute.charAt(0).toUpperCase() + info.attribute.slice(1)}: {info.attributeValue})
                  </span>
                </div>
                <div className="skill-item__rank">
                  Rank: <span className="skill-item__rank-value">{info.rank}</span>
                  {info.attributeModifier !== 0 && (
                    <span className="skill-item__attribute-modifier">
                      ({info.attributeModifier > 0 ? '+' : ''}{info.attributeModifier})
                    </span>
                  )}
                </div>
              </div>
              
              <div className="skill-item__description">
                {SKILL_DESCRIPTIONS[category]?.[skillName] || ""}
              </div>
              
              <div className="skill-item__controls">
                <SkillRankBar rank={info.rank} />
                
                <div className="skill-item__buttons">
                  <button
                    className="skill-item__button skill-item__button--decrease"
                    onClick={() => decreaseSkill(category, skillName)}
                    disabled={info.rank <= 0}
                  >
                    <Minus size={16} />
                  </button>
                  
                  <button
                    className="skill-item__button skill-item__button--increase"
                    onClick={() => increaseSkill(category, skillName)}
                    disabled={info.rank >= 5 || info.upgradeCost > remainingPoints}
                  >
                    <Plus size={16} />
                    <span className="skill-item__cost">
                      {info.rank < 5 ? info.upgradeCost : ''}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    );
  };
  
  return (
    <motion.div
      className="character-creation__content"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      <div className="skills-header">
        <h2 className="skills-header__title">Skill Selection</h2>
        <div className="skills-header__points">
          <span className="skills-header__points-label">Skill Points Remaining:</span>
          <span className="skills-header__points-value">{remainingPoints}</span>
        </div>
        
        <div className="skills-header__help">
          <button
            className="skills-header__help-button"
            onClick={() => setShowHelp(!showHelp)}
          >
            <Info size={18} />
            {showHelp ? 'Hide Help' : 'Show Help'}
          </button>
        </div>
        
        <div className="skills-header__actions">
          <button
            className="character-creation__button character-creation__button--secondary"
            onClick={resetSkills}
          >
            <RefreshCw size={16} />
            Reset Skills
          </button>
        </div>
      </div>
      
      {showHelp && (
        <motion.div
          className="skills-help"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <div className="skills-help__content">
            <h3>About Skills</h3>
            <ul>
              <li>Skills range from rank 0 (untrained) to 5 (legendary)</li>
              <li>Higher ranks cost more skill points to acquire</li>
              <li>Each skill is associated with an attribute that affects its effectiveness</li>
              <li>Your background provides starting bonuses to certain skills</li>
              <li>You'll earn more skill points as you level up</li>
            </ul>
            
            <h3>Skill Costs</h3>
            <div className="skills-help__costs">
              <div>Rank 0 → 1: <strong>1 point</strong></div>
              <div>Rank 1 → 2: <strong>1 point</strong></div>
              <div>Rank 2 → 3: <strong>2 points</strong></div>
              <div>Rank 3 → 4: <strong>3 points</strong></div>
              <div>Rank 4 → 5: <strong>5 points</strong></div>
            </div>
          </div>
        </motion.div>
      )}
      
      {remainingPoints < 0 && (
        <div className="skills-warning">
          <AlertTriangle size={18} />
          <span>
            You've spent too many skill points. Please remove some skills to continue.
          </span>
        </div>
      )}
      
      <div className="skills-grid">
        {Object.entries(skillInfo).map(([category, skills]) => (
          <SkillCategory key={category} category={category} skills={skills} />
        ))}
      </div>
      
      <div className="skills-footer">
        <div className="skills-footer__tip">
          <Info size={16} />
          <span>
            Tip: Focus on skills that complement your attributes. For example, if you have high strength,
            combat skills like One-Handed Weapons will be more effective.
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default Skills;